import { getAccessToken, getScopeHeaders } from '../lib/auth-storage';

const API_BASE = import.meta.env.VITE_LISTINGS_API ?? '/api/v1';

export type MarketplaceCode = 'amazon_in' | 'flipkart' | 'myntra' | 'ajio' | 'shopify' | 'woocommerce';

export type ListingContent = {
  title: string;
  subtitle?: string;
  brand?: string;
  description?: string;
  bullets?: string[];
  keywords?: string[];
  search_terms?: string[];
  images?: Array<{ id?: string; url: string; alt_text?: string; position?: number; is_main?: boolean }>;
};

export type MarketplaceListing = {
  id: string;
  product_id: string;
  marketplace: MarketplaceCode;
  marketplace_sku?: string;
  seller_sku?: string;
  marketplace_asin?: string;
  status: string;
  publishing_status: string;
  content: ListingContent;
  health: ListingHealth;
  scores: ListingScores;
  version: number;
  created_at: string;
  updated_at: string;
};

export type ListingHealth = {
  score: number;
  completeness: number;
  quality_score: number;
  seo_score: number;
  ai_listing_score: number;
  compliance_score: number;
  suppressed: boolean;
  missing_attributes?: string[];
  missing_images: boolean;
};

export type ListingScores = {
  overall: number;
  content: number;
  seo: number;
  compliance: number;
  ai: number;
  readiness: number;
};

export type ListingDashboard = {
  total_listings: number;
  published_listings: number;
  draft_listings: number;
  suppressed_listings: number;
  avg_health_score: number;
  avg_seo_score: number;
  by_marketplace?: Record<string, number>;
};

export type AIRecommendation = {
  listing_id: string;
  type: string;
  field: string;
  current?: string;
  suggested: string;
  confidence: number;
  reason?: string;
};

export type ListingComparison = {
  listing_a: MarketplaceListing;
  listing_b: MarketplaceListing;
  differences: Array<{ field: string; value_a: string; value_b: string; changed: boolean }>;
};

function headers(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...getScopeHeaders(),
    ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {}),
  };
}

async function listingsFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { ...headers(), ...(init?.headers as Record<string, string> | undefined) },
  });
  if (!res.ok) throw new Error(`Listings ${path} failed: ${res.status} ${await res.text()}`);
  const body = await res.json();
  return (body.data ?? body) as T;
}

export const listingsApi = {
  dashboard: () => listingsFetch<ListingDashboard>('/listings/dashboard'),

  list: (params?: { product_id?: string; marketplace?: string; status?: string; q?: string }) => {
    const q = new URLSearchParams(params as Record<string, string>).toString();
    return listingsFetch<MarketplaceListing[]>(`/listings${q ? `?${q}` : ''}`);
  },
  search: (params?: { q?: string; marketplace?: string; status?: string; limit?: string }) => {
    const q = new URLSearchParams(params as Record<string, string>).toString();
    return listingsFetch<MarketplaceListing[]>(`/listings/search?${q}`);
  },
  get: (id: string) => listingsFetch<MarketplaceListing>(`/listings/${id}`),
  create: (input: { product_id: string; marketplace: MarketplaceCode; content: ListingContent; marketplace_sku?: string }) =>
    listingsFetch<MarketplaceListing>('/listings', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, input: Partial<MarketplaceListing>) =>
    listingsFetch<MarketplaceListing>(`/listings/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
  delete: (id: string) => listingsFetch<void>(`/listings/${id}`, { method: 'DELETE' }),
  clone: (id: string) => listingsFetch<MarketplaceListing>(`/listings/${id}/clone`, { method: 'POST' }),
  approve: (id: string) => listingsFetch<MarketplaceListing>(`/listings/${id}/approve`, { method: 'POST' }),
  publish: (id: string) => listingsFetch<MarketplaceListing>(`/listings/${id}/publish`, { method: 'POST' }),
  archive: (id: string) => listingsFetch<MarketplaceListing>(`/listings/${id}/archive`, { method: 'POST' }),
  health: (id: string) => listingsFetch<ListingHealth>(`/listings/${id}/health`),
  recommendations: (id: string) => listingsFetch<AIRecommendation[]>(`/listings/${id}/recommendations`),
  bulkGenerate: (input: { product_ids: string[]; marketplace: MarketplaceCode }) =>
    listingsFetch<MarketplaceListing[]>('/listings/bulk-generate', { method: 'POST', body: JSON.stringify(input) }),
  bulkUpdate: (input: { listing_ids: string[]; patch: Record<string, unknown> }) =>
    listingsFetch<MarketplaceListing[]>('/listings/bulk-update', { method: 'POST', body: JSON.stringify(input) }),
  compare: (listingAId: string, listingBId: string) =>
    listingsFetch<ListingComparison>('/listings/compare', {
      method: 'POST',
      body: JSON.stringify({ listing_a_id: listingAId, listing_b_id: listingBId }),
    }),
};
